/**
 * Smart Column Detection
 * Maps arbitrary EHR column names to expected fields
 */

// Field aliases - ordered by priority (first match wins)
const FIELD_ALIASES = {
  // Name fields
  name: ['patient', 'name', 'fullname', 'patientname', 'patient_name', 'full_name'],
  firstName: ['firstname', 'first', 'fname', 'first_name', 'givenname', 'given_name'],
  lastName: ['lastname', 'last', 'lname', 'last_name', 'surname', 'familyname', 'family_name'],

  // Identifiers
  mrn: ['mrn', 'medical_record_number', 'medicalrecordnumber', 'patient_id', 'patientid', 'chart_number', 'chartnumber'],

  // Contact fields
  phone: ['phone', 'mobile', 'cell', 'mobilephone', 'telephone', 'tel', 'phonenumber', 'phone_number', 'contact_phone', 'cellphone', 'cell_phone'],
  email: ['email', 'emailaddress', 'email_address', 'mail', 'contact_email', 'e_mail'],

  // Clinical fields
  dob: ['dob', 'dateofbirth', 'date_of_birth', 'birthdate', 'birth_date', 'birthday'],
  lastVisit: ['lastvisit', 'last_visit', 'lastappt', 'last_appt', 'lastappointment', 'last_appointment', 'lastcontact', 'last_contact', 'lastseen', 'last_seen', 'dos', 'dateofservice', 'date_of_service'],
  nextVisit: ['nextvisit', 'next_visit', 'nextappt', 'next_appt', 'nextappointment', 'next_appointment'],
  diagnosis: ['diagnosis', 'dx', 'condition', 'primary_diagnosis', 'primarydiagnosis', 'diseasestate', 'disease_state'],
  notes: ['notes', 'comments', 'note', 'comment', 'remarks', 'chiefcomplaint', 'chief_complaint'],
  callouts: ['callouts', 'flags', 'alerts', 'warnings', 'status_flags'],

  // Status fields
  status: ['status', 'patient_status', 'patientstatus', 'currentstatus', 'current_status'],

  // Consent/preferences
  deceased: ['deceased', 'deceased?', 'is_deceased', 'isdeceased', 'dead'],
  consentToText: ['consenttotext', 'consent_to_text', 'consent to text?', 'sms_consent', 'smsconsent', 'text_consent', 'cantextsms', 'can_text'],

  // Provider
  provider: ['provider', 'physician', 'doctor', 'attending', 'pcp', 'primarycare', 'primary_care'],

  // Insurance
  insurance: ['insurance', 'payer', 'coverage', 'healthplan', 'health_plan', 'insurer', 'carrier', 'plan', 'insurance_plan', 'insuranceplan'],

  // Medications
  medications: ['medications', 'meds', 'rx', 'prescriptions', 'druglist', 'drug_list', 'medication', 'prescription', 'drugs', 'current_meds', 'currentmeds'],

  // EHR clinical detail columns (for flag translation)
  lastPsaDate: ['lastpsadate', 'last_psa_date', 'psadate', 'psa_date'],
  lastPsaValue: ['lastpsavalue', 'last_psa_value', 'psavalue', 'psa_value'],
  lastPsaRises: ['lastpsarises', 'last_psa_rises', 'psarises', 'psa_rises', 'psarising', 'psa_rising'],
  lastTtDate: ['lastttdate', 'last_tt_date', 'ttdate', 'tt_date'],
  lastTtValue: ['lastttvalue', 'last_tt_value', 'ttvalue', 'tt_value'],
  imaging: ['imaging', 'scans', 'imaginghistory', 'imaging_history'],
  adt: ['adt', 'adtmedication', 'adt_medication', 'adtmeds', 'adt_meds'],
  diseaseState: ['diseasestate', 'disease_state', 'diseasestage', 'disease_stage'],
};

/**
 * Normalize a column header for matching
 */
function normalizeHeader(header) {
  return header
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove special chars
    .trim();
}

/**
 * Calculate similarity between two strings (simple Levenshtein-based)
 */
function similarity(str1, str2) {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) return 1.0;

  const costs = [];
  for (let i = 0; i <= shorter.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= longer.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (shorter[i - 1] !== longer[j - 1]) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[longer.length] = lastValue;
  }

  return (longer.length - costs[longer.length]) / longer.length;
}

/**
 * Detect column mappings from CSV headers
 * @param {string[]} headers - Array of CSV column headers
 * @returns {Object} - Mapping of CSV header → our field name
 */
export function detectColumns(headers) {
  const mapping = {};
  const usedFields = new Set();

  for (const header of headers) {
    const normalized = normalizeHeader(header);
    let matched = false;

    // Try exact match first
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
      if (usedFields.has(field)) continue;

      if (aliases.includes(normalized)) {
        mapping[header] = field;
        usedFields.add(field);
        matched = true;
        break;
      }
    }

    // Try fuzzy match if no exact match
    if (!matched) {
      let bestMatch = null;
      let bestScore = 0.85; // Minimum threshold (raised from 0.8 to reduce false matches)

      // Skip fuzzy matching for very short headers — too ambiguous
      if (normalized.length >= 3) {
        for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
          if (usedFields.has(field)) continue;

          for (const alias of aliases) {
            // Also skip short aliases to avoid spurious matches (e.g. "dob" ↔ "dos")
            if (alias.length < 3) continue;
            const score = similarity(normalized, alias);
            if (score > bestScore) {
              bestScore = score;
              bestMatch = field;
            }
          }
        }
      }

      if (bestMatch) {
        mapping[header] = bestMatch;
        usedFields.add(bestMatch);
      } else {
        mapping[header] = null; // Unrecognized column
      }
    }
  }

  return mapping;
}

/**
 * Check if we found a patient identifier
 * @param {Object} mapping - Column mapping from detectColumns
 * @returns {boolean}
 */
export function hasPatientIdentifier(mapping) {
  const identifiers = ['name', 'firstName', 'mrn'];
  return Object.values(mapping).some(field => identifiers.includes(field));
}

/**
 * Get detection summary for preview
 * @param {Object} mapping - Column mapping from detectColumns
 * @returns {Object} - Summary of what was detected
 */
export function getDetectionSummary(mapping) {
  const fields = Object.values(mapping).filter(Boolean);

  return {
    hasName: fields.includes('name') || (fields.includes('firstName') && fields.includes('lastName')) || fields.includes('mrn'),
    hasContact: fields.includes('phone') || fields.includes('email'),
    hasPhone: fields.includes('phone'),
    hasEmail: fields.includes('email'),
    hasLastVisit: fields.includes('lastVisit'),
    hasDOB: fields.includes('dob'),
    hasCallouts: fields.includes('callouts'),
    hasDiagnosis: fields.includes('diagnosis'),
    hasNotes: fields.includes('notes'),
    hasDeceased: fields.includes('deceased'),
    hasConsentToText: fields.includes('consentToText'),
    hasMRN: fields.includes('mrn'),
    hasInsurance: fields.includes('insurance'),
    hasMedications: fields.includes('medications'),
    hasEhrDetails: ['lastPsaDate', 'lastPsaValue', 'lastTtDate', 'lastTtValue', 'imaging', 'adt', 'diseaseState'].some(f => fields.includes(f)),
    detectedFields: fields,
    unmappedColumns: Object.entries(mapping)
      .filter(([_, field]) => field === null)
      .map(([header]) => header),
  };
}
